import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";

const SellerModeration = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: shop } = useQuery({
    queryKey: ["my-shop-id", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("shops")
        .select("id, name")
        .eq("owner_id", user.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  const { data: products } = useQuery({
    queryKey: ["my-products-moderation", shop?.id, statusFilter],
    queryFn: async () => {
      if (!shop?.id) return [] as any[];
      let q = supabase
        .from("products")
        .select("id, title, status, price_cents, currency, stock")
        .eq("shop_id", shop.id)
        .order("created_at", { ascending: false });
      if (statusFilter !== "all") {
        q = q.eq("status", statusFilter);
      }
      const { data } = await q;
      return data ?? [];
    },
    enabled: !!shop?.id,
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("products")
        .update({ status })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-products-moderation"] });
    },
  });

  return (
    <main className="container mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-heading font-bold">Moderation</h1>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Filter:</label>
          <select
            className="border rounded px-3 py-2 bg-background"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
      </div>

      {!shop ? (
        <div className="text-muted-foreground">Create your shop first to manage products.</div>
      ) : !products?.length ? (
        <div className="text-muted-foreground">No products match this filter.</div>
      ) : (
        <div className="rounded-xl border divide-y">
          {products.map((p: any) => (
            <div key={p.id} className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium">{p.title}</div>
                <div className="text-sm text-muted-foreground">{p.currency} {(p.price_cents/100).toFixed(2)} · Stock {p.stock}</div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="border rounded px-2 py-1 bg-background text-sm"
                  value={p.status}
                  onChange={(e) => updateStatus.mutate({ id: p.id, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="hidden">Hidden</option>
                </select>
                <Button variant="outline" onClick={() => updateStatus.mutate({ id: p.id, status: p.status })} disabled>
                  Save
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
};

export default SellerModeration;
